// ─────────────────────────────────────────────────────────────────────────────
// dataCache, a tiny, dependency-free, band-scoped stale-while-revalidate cache
// for the screen data hooks.
//
// WHY: the nav tabs are client-side routes, so React unmounts a screen when you
// leave and mounts a fresh one when you return, every data hook's useEffect
// re-fires and re-fetches, so you watch it reload on every tab switch.
//
// This keeps the codebase's conventions intact:
//   • No new dependency (no React Query), a ~40-line hand-rolled helper.
//   • Server stays the SOURCE OF TRUTH, we ALWAYS refetch in the background;
//     the cache only removes the loading flash, it never replaces a real read.
//     apiGet's `cache: 'no-store'` is untouched (this is a hook-level cache).
//   • In-memory only (NO localStorage), matches the "don't persist user-data
//     reads client-side" rule; a hard reload always re-reads the server.
//   • BAND-KEYED, the cache key includes the active band (ac:active_band_id /
//     X-Band-Id via getCurrentBandId), so switching bands can NEVER serve
//     another band's data. This is the guard that makes caching safe here.
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useRef, useState } from 'react';
import { getCurrentBandId } from './auth';

/** Module-level cache: survives component unmount/remount (tab switches) but
 *  not a page reload. Keyed by base-key + active band + deps. */
const memCache = new Map<string, unknown>();

/* persist 2026-07-24 (Ryan: "all of them still doing this") — memory-only
   meant every page load started cold on every screen. Snapshots now write
   through to localStorage (band-scoped keys, same isolation guarantees) so a
   returning session paints REAL data on first frame and revalidates behind
   it. Deliberately relaxes the old "no client persistence" rule; purged on
   logout via purgePersistedCache() (wired into clearToken). */
const PERSIST_PREFIX = 'ac:sc1:';
function persistRead(key: string): unknown {
  try {
    const raw = localStorage.getItem(PERSIST_PREFIX + key);
    return raw === null ? undefined : (JSON.parse(raw) as unknown);
  } catch { return undefined; }
}
function persistWrite(key: string, val: unknown): void {
  try { localStorage.setItem(PERSIST_PREFIX + key, JSON.stringify(val)); }
  catch { /* quota / private mode — memory cache still works */ }
}
export function purgePersistedCache(): void {
  try {
    const doomed: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(PERSIST_PREFIX)) doomed.push(k);
    }
    doomed.forEach((k) => localStorage.removeItem(k));
  } catch { /* nothing to purge */ }
}
/** Mem-first, disk-second lookup; promotes disk hits into memory. */
function cacheGet(key: string): { hit: boolean; val: unknown } {
  if (memCache.has(key)) return { hit: true, val: memCache.get(key) };
  const disk = persistRead(key);
  if (disk !== undefined) { memCache.set(key, disk); return { hit: true, val: disk }; }
  return { hit: false, val: undefined };
}

/** focus-revalidate 2026-07-06: last fetch start per key, for the
 *  wake-throttle below. */
const lastFetchAt = new Map<string, number>();

/** One-shot: hide the host page's branded boot loader (#acLoader, see
 *  app-react.html) the first time ANY screen's data settles, i.e. the moment
 *  the app has something real to show. Mirrors legacy fetch-dedupe.js calling
 *  window.acHideLoader() once loading is idle. Safe no-op if the loader already
 *  hid (12s safety net) or isn't present. */
let bootLoaderHidden = false;
function hideBootLoader(): void {
  if (bootLoaderHidden) return;
  bootLoaderHidden = true;
  const w = window as unknown as { acHideLoader?: () => void };
  if (typeof w.acHideLoader === 'function') w.acHideLoader();
}

export interface CachedResult<T> {
  readonly data: T | undefined;
  readonly loading: boolean;
  readonly error: Error | null;
  /** Force a background refetch (e.g. after a save). */
  readonly reload: () => void;
}

/**
 * Band-scoped stale-while-revalidate over an async fetcher.
 *  - On mount: serve the last cached value for this (key, band, deps) INSTANTLY
 *    (no spinner), then ALWAYS refetch in the background and swap in fresh data.
 *  - On a fresh (uncached) key: show loading, fetch, cache.
 *  - `deps` change (or band change) → new key → its own cached snapshot + a
 *    fresh fetch (a band switch can't show the previous band's data).
 */
export function useCachedData<T>(
  baseKey: string,
  fetcher: () => Promise<T>,
  deps: ReadonlyArray<unknown> = [],
): CachedResult<T> {
  // Capture the band THIS render is scoped to. The fetcher reads the ambient
  // band id (getCurrentBandId) lazily when it runs, so on a fast artist switch a
  // fetch can resolve carrying a band we no longer show, we guard against that
  // at resolve time below so it can never land in the wrong band's cache.
  const band = getCurrentBandId() || 'self';
  const key = baseKey + '::' + band + '::' + JSON.stringify(deps);
  const [data, setData] = useState<T | undefined>(() => cacheGet(key).val as T | undefined);
  const [loading, setLoading] = useState<boolean>(() => !cacheGet(key).hit);
  const [error, setError] = useState<Error | null>(null);
  const [tick, setTick] = useState(0);
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  // ISOLATION GUARD (Bible 06.4): if the key changed, most importantly the
  // active band changed, reset `data` SYNCHRONOUSLY during render to the new
  // key's cached value (or undefined → loading). Without this, a screen that
  // stays mounted while the band switches would keep rendering the PREVIOUS
  // band's data until the background refetch lands, a stale cross-band read.
  // This is React's sanctioned "adjust state during render" pattern.
  const [prevKey, setPrevKey] = useState(key);
  if (prevKey !== key) {
    setPrevKey(key);
    const cg = cacheGet(key);
    setData(cg.val as T | undefined);
    setLoading(!cg.hit);
    setError(null);
  }

  /* focus-revalidate 2026-07-06 (found live: an opp deleted on iOS kept
     showing on an already-open desktop tab until a hard reload, band data is
     multi-device, so a tab that regains focus must re-ask the server).
     SWR-style: on window focus / tab-visible, if this key hasn't fetched in
     the last 30s, bump the tick → the main effect refetches. Throttled so
     alt-tabbing doesn't hammer the API. */
  useEffect(() => {
    const REVALIDATE_AFTER_MS = 30000;
    const onWake = (): void => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      if (Date.now() - (lastFetchAt.get(key) ?? 0) < REVALIDATE_AFTER_MS) return;
      setTick((n) => n + 1);
    };
    window.addEventListener('focus', onWake);
    document.addEventListener('visibilitychange', onWake);
    return () => {
      window.removeEventListener('focus', onWake);
      document.removeEventListener('visibilitychange', onWake);
    };
  }, [key]);

  useEffect(() => {
    let alive = true;
    // Serve cached instantly when present; otherwise show loading.
    const cg0 = cacheGet(key);
    if (cg0.hit) { setData(cg0.val as T); setLoading(false); } else { setLoading(true); }
    setError(null);
    // ALWAYS revalidate against the server (source of truth).
    lastFetchAt.set(key, Date.now()); // focus-revalidate 2026-07-06
    void fetcherRef.current().then((res) => {
      hideBootLoader();
      if (!alive) return;
      // RACE GUARD (fast artist switch): the fetcher read the ambient band id
      // when it ran; if the active band has since changed, this response is for
      // a band we no longer show, drop it so it can't poison this key's cache or
      // render under the wrong artist.
      if ((getCurrentBandId() || 'self') !== band) return;
      memCache.set(key, res);
      persistWrite(key, res);
      setData(res);
      setLoading(false);
    }).catch((err: unknown) => {
      hideBootLoader();
      if (!alive) return;
      setLoading(false);
      setError(err instanceof Error ? err : new Error(String(err)));
    });
    return () => { alive = false; };
  }, [key, tick]);

  return { data, loading, error, reload: () => setTick((n) => n + 1) };
}

/** Drop all cached data (e.g. on logout). */
export function clearDataCache(): void {
  memCache.clear();
}
